import {
    Dialog,
    DialogBody,
    DialogDisclosure,
    DialogDismiss,
    DialogFooter,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {ClockFastForwardIcon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {StyledProjectSuspensionsHistory, StyledTotalSuspensionDays} from './style'
import {ProjectExtended} from '../../types'
import {FormCardSection} from '@/components/ui/form-atoms/FormAtoms'
import {DetailsField} from '@/components/ui/details-atoms/DetailsAtoms'
import {Text} from '@/components/ui/text/Text'
import dayjs from 'dayjs'
import {suspendReasonToLabel} from '../../utils'
import {formatLocaleDate} from '@/dayjs'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {Button} from '@/components/ui/button/Button'

export const ProjectSuspensionsHistoryDialog = ({suspensions}: {suspensions: ProjectExtended['suspensions']}) => {
    const {t} = useTranslation()

    return (
        <DialogProvider>
            <DialogDisclosure
                render={
                    <StyledProjectSuspensionsHistory variant="ghost" size="sm">
                        <ClockFastForwardIcon />
                        {t('projects:suspensions_history')}
                    </StyledProjectSuspensionsHistory>
                }
            />
            <Dialog width="550px">
                <DialogHeader>
                    <DialogHeading>{t('projects:suspensions_history')}</DialogHeading>
                    <DialogDismiss />
                </DialogHeader>
                <ScrollArea style={{maxHeight: 400}} scrollbar={<Scrollbar />}>
                    <DialogBody>
                        <StyledTotalSuspensionDays>
                            {t('projects:total_suspension_days', {
                                count: suspensions?.reduce(
                                    (acc, suspension) =>
                                        acc +
                                        (suspension.endDate
                                            ? dayjs(suspension.endDate).diff(dayjs(suspension.startDate), 'day')
                                            : dayjs().diff(dayjs(suspension.startDate), 'day')),
                                    0
                                )
                            })}
                        </StyledTotalSuspensionDays>
                        {suspensions?.length &&
                            suspensions.map(suspension => (
                                <FormCardSection columns={3} gap={2} padding={2} key={suspension.startDate}>
                                    <DetailsField>
                                        <Text size="textSm" weight="400" palette="neutral" shade="950">
                                            {t('projects:start_date')}
                                        </Text>
                                        <Text size="textSm" weight="400" palette="neutral" shade="600">
                                            {formatLocaleDate(suspension.startDate, 'DD/MM/YYYY')}
                                        </Text>
                                    </DetailsField>
                                    <DetailsField>
                                        <Text size="textSm" weight="400" palette="neutral" shade="950">
                                            {t('agenda:confirmation_end_date')}
                                        </Text>
                                        <Text size="textSm" weight="400" palette="neutral" shade="600">
                                            {suspension.endDate
                                                ? formatLocaleDate(suspension.endDate, 'DD/MM/YYYY')
                                                : t('commons:ongoing')}
                                        </Text>
                                    </DetailsField>
                                    <DetailsField>
                                        <Text size="textSm" weight="400" palette="neutral" shade="950">
                                            {t('commons:duration')}
                                        </Text>
                                        <Text size="textSm" weight="400" palette="neutral" shade="600">
                                            {suspension.endDate
                                                ? t('commons:days_count', {
                                                      count: dayjs(suspension.endDate).diff(
                                                          dayjs(suspension.startDate),
                                                          'day'
                                                      )
                                                  })
                                                : t('commons:days_count', {
                                                      count: dayjs().diff(dayjs(suspension.startDate), 'day')
                                                  })}
                                        </Text>
                                    </DetailsField>
                                    <DetailsField style={{gridColumn: 'span 3'}}>
                                        <Text size="textSm" weight="400" palette="neutral" shade="950">
                                            {t('projects:suspend_reason')}
                                        </Text>
                                        <Text size="textSm" weight="400" palette="neutral" shade="600">
                                            {t(suspendReasonToLabel[suspension.reason])}
                                        </Text>
                                    </DetailsField>
                                    <DetailsField style={{gridColumn: 'span 3'}}>
                                        <Text size="textSm" weight="400" palette="neutral" shade="950">
                                            {t('commons:notes')}
                                        </Text>
                                        <Text size="textSm" weight="400" palette="neutral" shade="600">
                                            {suspension.notes}
                                        </Text>
                                    </DetailsField>
                                </FormCardSection>
                            ))}
                    </DialogBody>
                </ScrollArea>
                <DialogFooter style={{justifyContent: 'end'}}>
                    <DialogDismiss render={<Button variant="secondary" />}>{t('commons:close')}</DialogDismiss>
                </DialogFooter>
            </Dialog>
        </DialogProvider>
    )
}
